import { CATEGORY_MARK } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/format';
import {
    filterOriginStyle, generateCustomStyles,
    getShapeAttr,
    NO_SUPPORT_FILL_SYMBOL,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style';
import { Marker } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/marker';
import {
    DataLabel,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/data-label';
import { STMarkerStyleDmlC } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import type { GeometryType, IChartRendererConfig, IGeometryOptions, IComboChartConfig, IChartAdjust, IChartLabel } from '@tencent/chart/lib/chart-ui/interface';
import { CHART_TYPE } from '@tencent/chart/lib/chart-ui/interface';
import {
    Trendline,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/trendline';
import isEmpty from 'lodash/isEmpty';
import { Coordinate } from './element/coordinate';
import { Axis } from './element/axis';
import type {
    ChartUIDataItem,
    ChartUILabel,
    ChartUIMarker,
    ChartUISeries, CustomStyles,
    ShapeStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import type {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import type {
    ChartLayoutInfo,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-component.interface';
import type { MarkerProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/marker';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import {
    IChartDataItem,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-item/data-item.interface';
import type { ChartContext } from '@src/slide/domain/ui-model/element-modeles/chart-models/context';
import {
    IMarkerProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/marker/marker.interface';
import {
    ShapeOriginStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';

export interface IChartConfigHelper {
    /**
     * 获取图表绘制信息。目前单图表和组合图表统一处理为多图表
     */
    getConfig(): IComboChartConfig;
}

export abstract class BaseChartConfigHelper implements IChartConfigHelper {
    public readonly id: string;
    public abstract get type(): CHART_TYPE;

    /**
     * 处理后的图表数据点数据
     */
    protected data: Array<ChartUIDataItem>;
    protected series: ChartUISeries[];

    private axis: Axis;

    public constructor(protected context: ChartContext, protected layout: ChartLayoutInfo) {
        this.id = `${this.subChart.type}_${this.subChart.id}`;
    }


    // 原本在constructor中自执行的逻辑，移到init中处理
    public init() {
        this.normalize();

        return this;
    }

    public getConfig(): IComboChartConfig {
        const baseRenderConfig = this.getRenderConfig();

        // 重要！！！
        // 将所有的图表都在chart库那层视为组合图处理，这样做的原因：
        // 一个图表除了自己的geometry外，还会包含很多其他的元素：趋势线、误差线等。这些元素使用的数据点和原本的图表数据点不同，所以需要在另一个view来绘制。
        // 这些元素geometry和图表本身的geometry在 g2那一层本质上是一样的，和处理组合图表没有区别。所以最终给g2的，所有的图表都是组合图。每个图表都会包含一个chartRenderConfigs的数组，
        // 在chart库中根据chartRenderConfigs中所有的内容，都分别创建一个view去绘制
        const renderConfigs: IChartRendererConfig[] = [baseRenderConfig];

        if (this.subChart.trendlines?.length) {
            const trendLineConf = new Trendline(this.subChart.trendlines).getConfig(baseRenderConfig);
            renderConfigs.push(trendLineConf);
        }

        // 同时拥有坐标轴标题和坐标轴显示单位，处理坐标轴标题
        const yAxis = this.axis.yAxisProperty;
        const isYAxisValueType = yAxis?.type === 'value';
        const isYAxisDisplay = !yAxis?.isDelete;
        const hasYAxisUnitLabel = !isEmpty(yAxis?.units?.label);
        const hasYAxisTitle = !isEmpty(yAxis?.title);
        if (isYAxisValueType && hasYAxisUnitLabel && hasYAxisTitle && isYAxisDisplay) {
            const axisTitleConfig = this.createAxisTitleConf(baseRenderConfig);
            renderConfigs.push(axisTitleConfig);
        }

        return {
            type: CHART_TYPE.CUSTOM_COMBO_CHART,
            subChartRendererConfigs: renderConfigs,
        };
    }

    protected createAxisTitleConf = (baseRenderConfig: IChartRendererConfig): IChartRendererConfig => {
        const yAxis = this.axis.yAxisProperty;
        const config = { ...baseRenderConfig };
        if (typeof config.yAxis !== 'boolean' && yAxis?.title) {
            config.yAxis = Axis.getAxisConfigWithTitle(this.context, config.yAxis!, yAxis.title);
        }
        config.legend = false;
        return config;
    };

    // 获取chart库所用的最终renderConfig数据
    protected getRenderConfig(): IChartRendererConfig {
        const config: IChartRendererConfig = {
            type: this.type,
            id: this.id,
            limitInPlot: this.getLimitInPlot(),
            limitInPlotAppendMargin: this.getLimitInPlotAppendMargin(),
            data: this.data,
            geometryStyle: this.createIdMappingCfg<ShapeStyle>((chartDataItem) => {
                const shapeAttr = this.getGeometryStyle(chartDataItem);
                return this.filterShapeAttr(shapeAttr);
            }),
            geometryOptions: this.getGeometryOptions(),
            geometryShape: this.getGeometryShape ? {
                fields: ['name'],
                callback: this.getGeometryShape,
            } : undefined,
            seriesColor: this.getSeriesColor(),
            geometrySize: this.getGeometrySize
                ? this.createIdMappingCfg<number>(chartDataItem => this.getGeometrySize(chartDataItem))
                : undefined,
            adjusts: this.getAdjustsConfig(),
            serLines: this.getSerLines(),
            label: this.getLabelMappingConfig() as IChartLabel,
            theme: this.getThemeConfig(),
            coordinate: this.getCoordinateConfig(),
            ...this.getAxisConfig(),
        };

        return config;
    }

    protected normalize() {
        this.normalizeSeries();
        this.normalizeData();
    }

    protected normalizeSeries() {
        this.series = this.subChart.series
            .map((ser): ChartUISeries => {
                const { fill, border, ...rest } = getShapeAttr(ser.props);

                return {
                    name: ser.name,
                    index: ser.idx || 0,
                    fill,
                    border,
                    ...rest,
                    values: ser.dataList.map(item => item.valuePoint?.value),
                    categories: ser.dataList.map(item => item.categoryPoint?.value),
                    type: this.subChart.type,
                    trendlines: ser.trendlines,
                    marker: this.getMarkerConfig(ser.marker as MarkerProperty),
                    disable: ser.disable,
                    seriesId: ser.seriesId,
                };
            });
    }

    protected normalizeData() {
        this.data = this.subChart.data?.map((data, index) => this.normalizeDataItem(data, index));
    }

    protected normalizeDataItem(dataItem: IChartDataItem, dataItemIndex: number): ChartUIDataItem {
        const { type, ser, marker, label, category,
            categoryIndex, originCategory, value, originValue, dataPoint, serIndex } = dataItem;

        const chartDataItemId = `${type}_${ser.idx}_${dataItemIndex}`;

        const categoryVal = typeof category === 'number'
            ? category : `${category}${CATEGORY_MARK}${categoryIndex}`;

        return {
            name: ser.name,
            serIndex,
            // category可能同名，但是一般不会是日期坐标轴或者数字。因为现在的number类型会存在需要format或者Number，不能全部加隐藏索引
            // 所以目前先再非number类型上加隐藏索引，用于支持同名的category进行分组
            category: categoryVal,
            originCategory,
            categoryIndex,
            value,
            originValue,
            ...getShapeAttr(dataItem.props, { isLine: this.isLine() }),
            type,
            id: chartDataItemId,    // 这个id是用于在g2的callback中，通过id找到对应的处理后的dataItem
            label: this.getDataLabelItemConfig({ label }),
            marker: this.getMarkerConfig(marker),
            seriesId: ser.seriesId,
            dataId: dataItem.id,    // 这个dataItemId是在model层生成的，用于ui的dataItem关联到model的dataListItem
            dataPointStyle: dataPoint && getShapeAttr(dataPoint.props),
        };
    }

    /**
     * 是线条还是边框。会导致linecap不一样。
     * @protected
     */
    protected isLine() {
        return false;
    }

    protected getMarkerConfig(marker?: IMarkerProperty): ChartUIMarker | undefined {
        if (marker && marker.symbol !== STMarkerStyleDmlC.none) {
            return new Marker(marker).getConfig();
        }

        return undefined;
    }

    protected getCoordinateConfig() {
        const coordinate = new Coordinate(this.subChart);
        return coordinate.getConfig();
    }

    /**
     * 数据标签配置
     */
    protected getLabelMappingConfig() {
        return DataLabel.getFiledMappingConfig(this.data, this.subChart);
    }

    protected getDataLabelItemConfig(param: {
        label?: IDataLabelProperty
    }): ChartUILabel | boolean {
        const { label } = param;
        if (!label) return false;

        return this.getDataLabel(label).getConfig();
    }

    protected getDataLabel(label: IDataLabelProperty): DataLabel {
        return new DataLabel(label, this.context);
    }

    protected getAxis() {
        return new Axis(this.subChart, this.context.getChartModel()).init();
    }

    protected getAxisConfig() {
        const axis = this.getAxis();
        this.axis = axis;

        const { xScale, yScale } = axis.getScaleConfig();
        const { xAxis, yAxis } = axis.getAxisConfig();

        return {
            xScale,
            yScale,
            xAxis,
            yAxis,
        };
    }

    protected isShowMarker(list: any[]): boolean {
        return list.some(this.hasMarker);
    }

    protected hasMarker(item): boolean {
        return item.marker && item.marker.symbol !== STMarkerStyleDmlC.none;
    }

    /**
     *  用于动态获取图形样式属性，应用于g2的geometry.style api
     *  geometryType: 组合图中实际对应的geometry实例的key
     *  TODO 后面看是不是把geometryStyle也抽到一个模块里
     */
    protected getGeometryStyle(chartDataItem: ChartUIDataItem, geomType?: GeometryType): ShapeStyle {
        const { fill, border } = chartDataItem || {};

        return {
            fill,
            lineWidth: border?.lineWidth,
            lineDash: border?.lineDash,
            stroke: border?.stroke,
            lineJoin: border?.lineJoin,
            lineCap: border?.lineCap,
            customStyles: this.getGeometryCustomStyles(chartDataItem),
        };
    }

    /**
     * 获取原始样式信息。子类可继承该方法，允许对数据做二次处理
     * @param chartDataItem
     * @protected
     */
    protected getGeometryCustomStyles(chartDataItem: ChartUIDataItem): CustomStyles {
        return generateCustomStyles(chartDataItem);
    }

    protected getGeometryOptions(): IGeometryOptions | undefined  {
        return;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected getGeometryShape: (name: string) => string;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected getGeometrySize: (chartDataItem: ChartUIDataItem) => number;

    /**
     * 获取图形样式属性，应用于g2的geometry.color的视觉通道分组和映射
     */
    protected getSeriesColor(): IChartRendererConfig['seriesColor'] {
        return this.series.map(ser => ser.border?.stroke || '');
    }

    protected getAdjustsConfig(): IChartAdjust[] {
        return [];
    }

    protected filterShapeAttr(shapeAttr) {
        if (shapeAttr?.fill === NO_SUPPORT_FILL_SYMBOL) {
            delete shapeAttr.fill;

            return shapeAttr;
        }

        return shapeAttr;
    }

    /**
     *  是否对超出坐标系范围的 Geometry 进行剪切
     */
    protected getLimitInPlot() {
        return true;
    }

    // TODO 系列线是个数组。目前只处理了第一个
    protected getSerLines(): ShapeStyle {
        const { serLines } = this.subChart;
        if (serLines) {
            const { fill, border, ...rest } = getShapeAttr(serLines[0]?.props);
            return this.filterShapeAttr({
                fill,
                ...border,
                customStyles: generateCustomStyles(rest),
            });
        }

        return {};
    }

    protected get subChart(): IBaseSubChartProperty {
        return this.context.getSubChartModel()?.getSubChartProperty();
    }

    protected getThemeConfig(): IChartRendererConfig['theme'] {
        return {
            styleSheet: {},
        };
    }

    // 超出坐标系范围进行裁剪时，是否需要在垂直方向预留间距，防止折线图折线再画布顶部时被裁显示不全等场景
    protected getLimitInPlotAppendMargin() {
        return true;
    }

    protected createIdMappingCfg = <T = ShapeStyle>(callback: (item: ChartUIDataItem) => T,
        data?: Array<ChartUIDataItem>) => ({
        fields: ['id'],
        callback: (id) => {
            const chartData = data || this.data;
            const chartDataItem = chartData.find(item => item.id === id);
            if (!chartDataItem) return {};

            return callback(chartDataItem);
        },
    });

    protected  createFieldMappingCfg = <T>(callback: (args: string) => T, field = 'id') => ({
        fields: [field],
        callback,
    });
}
